import "@/i18n";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, Lock, User as UserIcon, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/useAuth";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign In | Bacha Way Travel" },
      { name: "description", content: "Sign in to your Bacha Way Travel account to manage bookings and save 10% on every transfer." },
      { property: "og:title", content: "Sign in — Bacha Way Travel" },
      { property: "og:description", content: "Sign in to manage your bookings and save on every transfer." },
      { property: "og:url", content: "https://bachawaytravel.com/login" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/login" }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState<null | "google" | "apple" | "email">(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate({ to: "/booking", replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  async function oauth(provider: "google" | "apple") {
    setError(null);
    setBusy(provider);
    try {
      const res = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });
      if (res.error) {
        setError(res.error.message ?? "Sign-in failed.");
        setBusy(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed.");
      setBusy(null);
    }
  }

  async function emailSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy("email");
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/booking`,
            data: { full_name: fullName },
          },
        });
        if (error) throw error;
        setInfo(t("auth.checkEmail"));
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/booking", replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Authentication failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 py-12 md:py-20">
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <img src={logo} alt="Bacha Way Travel" className="h-12 w-12 rounded-full" />
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl text-foreground">
          {mode === "signup" ? t("auth.createAccount") : t("auth.welcomeBack")}
        </h1>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary">
          <Sparkles className="h-3.5 w-3.5" /> {t("auth.memberPerk")}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-background/60 p-6 sm:p-8 space-y-4">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => oauth("google")}
          className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium hover:bg-muted transition disabled:opacity-60"
        >
          {busy === "google" ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
          {t("auth.continueGoogle")}
        </button>

        <button
          type="button"
          disabled={busy !== null}
          onClick={() => oauth("apple")}
          className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-foreground text-background px-5 py-3 text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
        >
          {busy === "apple" ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleIcon />}
          {t("auth.continueApple")}
        </button>

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          {t("auth.or")}
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={emailSubmit} className="space-y-3">
          {mode === "signup" && (
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                required
                placeholder={t("auth.fullName")}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-full border border-border bg-background pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="email"
              required
              placeholder={t("auth.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-full border border-border bg-background pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="password"
              required
              minLength={6}
              placeholder={t("auth.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-full border border-border bg-background pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {info && <p className="text-sm text-primary">{info}</p>}

          <button
            type="submit"
            disabled={busy !== null}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {busy === "email" && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "signup" ? t("auth.createAccountCta") : t("auth.signInCta")}
          </button>
        </form>

        <div className="text-center text-sm text-muted-foreground">
          {mode === "signup" ? (
            <>
              {t("auth.haveAccount")}{" "}
              <button onClick={() => { setMode("signin"); setError(null); setInfo(null); }} className="text-primary font-medium hover:underline">
                {t("auth.signIn")}
              </button>
            </>
          ) : (
            <>
              {t("auth.noAccount")}{" "}
              <button onClick={() => { setMode("signup"); setError(null); setInfo(null); }} className="text-primary font-medium hover:underline">
                {t("auth.signUp")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.12A6.86 6.86 0 015.48 12c0-.74.13-1.46.36-2.12V7.04H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.365 1.43c0 1.14-.42 2.22-1.18 3.04-.88.97-2.31 1.7-3.5 1.6-.15-1.12.41-2.27 1.16-3.05.84-.88 2.27-1.55 3.52-1.59zm3.77 17.18c-.69 1.59-1.02 2.3-1.9 3.71-1.24 1.97-2.99 4.43-5.15 4.45-1.92.02-2.41-1.25-5.02-1.23-2.61.02-3.16 1.25-5.08 1.23C1.83 26.75.18 24.55-1.06 22.58c-3.47-5.51-3.83-11.99-1.69-15.43 1.52-2.44 3.92-3.87 6.17-3.87 2.29 0 3.73 1.26 5.62 1.26 1.84 0 2.96-1.26 5.61-1.26 2 0 4.12 1.09 5.63 2.97-4.95 2.71-4.15 9.78.85 12.36z"/>
    </svg>
  );
}
